import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle, Star, ShoppingCart, Loader2 } from 'lucide-react';
import { productsAPI, paymentsAPI } from '../services/api';
import { HANDBOOK_ENABLED } from '../lib/featureFlags';

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const productsData = await productsAPI.getAll();
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
      // Fallback to empty array on error
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (product) => {
    try {
      setProcessingPayment(product.id);

      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/payment-success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/payment-canceled`;

      const checkoutData = await paymentsAPI.createProductCheckout(
        product.id,
        successUrl,
        cancelUrl,
        { product_name: product.name, source: 'products_page' }
      );

      window.location.href = checkoutData.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to create checkout session. Please try again.');
    } finally {
      setProcessingPayment(null);
    }
  };

  if (loading) {
    return (
      <section id="products" className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading products...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="products" className="py-14 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Explore Ready-to-Use HR Templates & Toolkits
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Instant access to professionally crafted, fully editable HR documents designed for small and midsize businesses. No subscriptions required, purchase once, use forever.
          </p>
        </div>

        {/* Products Grid */}
        <div className="space-y-12">
          {/* Ultimate Bundle - Featured */}
          {products.filter(product => product.id === 'ultimate-hr-bundle').map((product) => (
            <Card 
              key={product.id} 
              id="ultimate-hr-bundle"
              className="relative hover:shadow-2xl transition-shadow duration-300 ring-2 ring-yellow-400 scale-105 bg-gradient-to-r from-yellow-50 to-orange-50"
            >
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-gray-900 font-semibold px-6 py-2">
                <Star className="w-4 h-4 mr-1" />
                BEST VALUE - Save $423
              </Badge>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
                <div>
                  <CardHeader className="p-0 pb-4">
                    <CardTitle className="text-3xl font-bold text-gray-900">
                      {product.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 text-lg mt-2">
                      {product.description}
                    </CardDescription>
                    <div className="mt-4">
                      <span className="text-2xl text-gray-400 line-through mr-3" data-testid="ultimate-regular-price">$599</span>
                      <span className="text-5xl font-bold text-yellow-600" data-testid="ultimate-launch-price">
                        ${product.price}
                      </span>
                      <span className="text-gray-500 ml-2">launch price · one-time</span>
                      <div className="text-sm text-gray-600 mt-1">
                        Individual bundles would cost $922 - Save $423!
                      </div>
                    </div>
                  </CardHeader>
                </div>
                
                <div>
                  <CardContent className="p-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {product.features.slice(0, 12).map((feature, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      className="w-full mt-6 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 py-3 text-lg"
                      size="lg"
                      onClick={() => window.location.href = '/bundle/ultimate-hr-bundle'}
                    >
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Purchase Now • $499
                    </Button>
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      All sales are final. Because our products are delivered digitally and downloaded immediately, DriveHR does not offer refunds. Please review the product details carefully before purchasing.
                    </p>
                  </CardContent>
                </div>
              </div>
            </Card>
          ))}

          {/* Individual Bundles */}
          <div id="individual-bundles">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">Individual HR Bundles</h3>
            <div className="text-gray-600 text-center mb-8 max-w-3xl mx-auto space-y-3">
              <p>
                <strong>Targeted, ready-to-use solutions</strong> for your most urgent HR needs without the full library price. 
                Start with the toolkit that matters most today and add others anytime as your business grows.
              </p>
              <p>
                Every bundle includes <strong>professionally written, fully editable templates</strong> you can start using immediately.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.filter(product => product.id !== 'ultimate-hr-bundle' && product.id !== 'handbook-builder').map((product) => (
                <Card 
                  key={product.id} 
                  className="relative hover:shadow-xl transition-shadow duration-300 h-full flex flex-col"
                >
                  <CardHeader className="text-center pb-4 flex-shrink-0">
                    <CardTitle className="text-xl font-bold text-gray-900">
                      {product.name}
                    </CardTitle>
                    <CardDescription className="text-gray-600 mt-2 text-sm">
                      {product.description}
                    </CardDescription>
                    <div className="mt-4">
                      <span className="text-3xl font-bold text-blue-600">
                        ${product.price}
                      </span>
                      <span className="text-gray-500 ml-2">one-time</span>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-grow">
                    <ul className="space-y-2">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-4 flex flex-col space-y-2">
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                      size="lg"
                      onClick={() => handlePurchase(product)}
                      disabled={processingPayment === product.id}
                      data-testid={`homepage-bundle-purchase-${product.id}`}
                    >
                      {processingPayment === product.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin flex-shrink-0" />
                          Redirecting to secure checkout...
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Purchase Now
                        </>
                      )}
                    </Button>
                    <a
                      href={
                        {
                          'hiring-onboarding': '/bundle/hiring-onboarding',
                          'policies-bundle': '/bundle/policies',
                          'performance-development': '/bundle/performance-development',
                          'benefits-compensation': '/bundle/benefits-compensation',
                          'termination-offboarding': '/bundle/termination-offboarding',
                          'compliance-legal': '/bundle/compliance-legal',
                          'culture-dei-engagement': '/bundle/culture-dei-engagement',
                          'hr-operations': '/bundle/hr-operations'
                        }[product.id] || '#'
                      }
                      className="text-sm text-blue-600 hover:text-blue-800 text-center hover:underline"
                    >
                      View Full Product Details →
                    </a>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      All sales are final. Digital products are non-refundable.
                    </p>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>

          {/* Custom Employee Handbook Creation Section (feature-gated) */}
          {HANDBOOK_ENABLED && (
            <>
              <div className="text-center mb-12 mt-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Need a Handbook Built the Right Way?</h3>
                <p className="text-gray-600 max-w-3xl mx-auto">
                  If your business needs a clear, compliant handbook, DriveHR will create it for you, professionally, accurately, and without the hassle.
                </p>
              </div>

              {/* Handbook Service - Premium Service */}
              {products.filter(product => product.id === 'handbook-builder').map((product) => (
                <Card 
                  key={product.id} 
                  className="relative hover:shadow-2xl transition-shadow duration-300 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200"
                >
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white px-4 py-1">
                    Premium Service
                  </Badge>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
                    <div>
                      <CardHeader className="p-0 pb-4">
                        <CardTitle className="text-3xl font-bold text-gray-900">
                          {product.name}
                        </CardTitle>
                        <CardDescription className="text-gray-600 text-lg mt-2">
                          {product.description}
                        </CardDescription>
                        <div className="mt-4">
                          <span className="text-4xl font-bold text-purple-600">
                            ${product.price}
                          </span>
                          <span className="text-gray-500 ml-2">one-time</span>
                        </div>
                      </CardHeader>
                    </div>
                    
                    <div>
                      <CardContent className="p-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          {product.features.map((feature, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                        <Button 
                          className="w-full mt-6 bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 py-3 text-lg"
                          size="lg"
                          onClick={() => window.location.href = '/handbook-builder'}
                        >
                          <ShoppingCart className="w-5 h-5 mr-2" />
                          Purchase Now • $649
                        </Button>
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          All sales are final. DriveHR does not offer refunds. Please review the product details carefully before purchasing.
                        </p>
                        
                        {/* Legal Disclaimer */}
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
                          <p className="text-xs text-gray-600 leading-relaxed">
                            <strong>Legal Disclaimer:</strong> This service is based on current federal and state HR best practices and compliance standards. DriveHR does not provide legal advice. You are encouraged to review your final handbook with qualified legal counsel prior to implementation.
                          </p>
                        </div>
                      </CardContent>
                    </div>
                  </div>
                </Card>
              ))}
            </>
          )}
        </div>
      </div>
    </section>
  );
};